import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaGlobe,
  FaFacebookF,
  FaInstagram,
  FaSearch,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";

import "./OrderTrackingPage.css";

const GET_ORDER_FUNCTION =
  "https://xrxkmksbutmuouddtzcm.supabase.co/functions/v1/get-order-status";

const SUBMIT_ANSWER_FUNCTION =
  "https://xrxkmksbutmuouddtzcm.supabase.co/functions/v1/submit-customer-answer";

const OrderTrackingPage = () => {
  const { t, i18n } = useTranslation();

  const [showLangMenu, setShowLangMenu] = useState(false);

  const [orderNo, setOrderNo] = useState("");
  const [order, setOrder] = useState(null);

  const [customerAnswer, setCustomerAnswer] = useState("");

  const [loading, setLoading] = useState(false);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const isArabic = i18n.language === "ar";

  useEffect(() => {
    document.dir = isArabic ? "rtl" : "ltr";
  }, [isArabic]);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);

    document.dir = lang === "ar" ? "rtl" : "ltr";

    setShowLangMenu(false);
  };

  const handleTrackOrder = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setOrder(null);
    setCustomerAnswer("");

    const cleanOrderNo = orderNo.trim().toUpperCase();

    if (!cleanOrderNo) {
      setErrorMessage(
        t("Please enter your order number.")
      );
      return;
    }

    if (loading) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        GET_ORDER_FUNCTION,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            order_no: cleanOrderNo,
          }),
        }
      );

      const data = await response.json();

      console.log(
        "Order tracking response:",
        data
      );

      if (!response.ok || !data.success) {
        throw new Error(
          data.message ||
            t(
              "Unable to find your order."
            )
        );
      }

      setOrder(data.order);

      if (data.order.customer_answer) {
        setCustomerAnswer(
          data.order.customer_answer
        );
      }
    } catch (error) {
      console.error(
        "Order tracking error:",
        error
      );

      setErrorMessage(
        error.message ||
          t(
            "Something went wrong. Please try again."
          )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!order) {
      return;
    }

    if (!customerAnswer.trim()) {
      setErrorMessage(
        t("Please enter your answer.")
      );
      return;
    }

    if (submittingAnswer) {
      return;
    }

    setSubmittingAnswer(true);

    try {
      const response = await fetch(
        SUBMIT_ANSWER_FUNCTION,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            order_no: order.order_no,
            customer_answer:
              customerAnswer.trim(),
          }),
        }
      );

      const data = await response.json();

      console.log(
        "Submit customer answer response:",
        data
      );

      if (!response.ok || !data.success) {
        throw new Error(
          data.message ||
            t(
              "Unable to submit your answer."
            )
        );
      }

      setOrder((previous) => ({
        ...previous,
        customer_answer:
          data.customer_answer,
      }));

      setCustomerAnswer(
        data.customer_answer
      );

      setSuccessMessage(
        t("Your answer has been submitted successfully.")
      );
    } catch (error) {
      console.error(
        "Submit customer answer error:",
        error
      );

      setErrorMessage(
        error.message ||
          t(
            "Something went wrong. Please try again."
          )
      );
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const hasCustomerAnswer =
    Boolean(
      order?.customer_answer &&
        order.customer_answer.trim()
    );

  return (
    <div className="ordertracking-container">

      <div
        className="ambient-glow"
        aria-hidden="true"
      />

      <div
        className="ordertracking-grid"
        aria-hidden="true"
      />

      <div className="about-topbar">
        <div className="lang-switcher">

          <button
            type="button"
            className="lang-toggle"
            onClick={() =>
              setShowLangMenu(
                (value) => !value
              )
            }
            aria-label={t(
              "Toggle language menu"
            )}
            aria-expanded={showLangMenu}
          >
            <FaGlobe size={16} />

            <span>
              {isArabic ? "AR" : "EN"}
            </span>
          </button>

          {showLangMenu && (
            <div className="language-dropdown">

              <div
                onClick={() =>
                  changeLanguage("en")
                }
              >
                {t("English")}
              </div>

              <div
                onClick={() =>
                  changeLanguage("ar")
                }
              >
                {t("Arabic")}
              </div>

            </div>
          )}

        </div>
      </div>

      <main className="ordertracking-main">

        <div
          className="ordertracking-system-label reveal"
          style={{
            "--delay": "0.05s",
          }}
        >
          <span className="system-dot" />

          <span>
            {t("EMKA // ORDER TRACKING")}
          </span>
        </div>

        <div
          className="ordertracking-heading reveal"
          style={{
            "--delay": "0.1s",
          }}
        >
          <span className="eyebrow">
            {t("// PROJECT STATUS")}
          </span>

          <h1>
            {t("Track Your Order")}
          </h1>

          <p>
            {t(
              "Enter your order number to view the latest status of your project."
            )}
          </p>
        </div>

        <div
          className="ordertracking-search-card reveal"
          style={{
            "--delay": "0.2s",
          }}
        >
          <label htmlFor="order-number">
            {t("Order Number")}
          </label>

          <div className="ordertracking-input-row">

            <input
              id="order-number"
              type="text"
              value={orderNo}
              onChange={(e) =>
                setOrderNo(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleTrackOrder();
                }
              }}
              placeholder="EMK-XXXXXX"
              autoComplete="off"
              disabled={loading}
            />

            <button
              type="button"
              onClick={handleTrackOrder}
              disabled={loading}
            >
              <FaSearch />

              <span>
                {loading
                  ? t("Checking...")
                  : t("Track Order")}
              </span>
            </button>

          </div>
        </div>

        {errorMessage && (
          <div
            className="ordertracking-message error-message reveal"
            style={{
              "--delay": "0.1s",
            }}
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        {order && (
          <div
            className="order-result-card reveal"
            style={{
              "--delay": "0.1s",
            }}
          >

            <div className="order-result-header">

              <div>
                <span className="eyebrow">
                  {t("// ORDER FOUND")}
                </span>

                <h2>
                  {t("Welcome")},{" "}
                  {order.user_name}
                </h2>
              </div>

              <div className="order-status">
                {order.status}
              </div>

            </div>

            <div className="order-info-grid">

              <div className="order-info-item">
                <span>
                  {t("Order Number")}
                </span>

                <strong>
                  {order.order_no}
                </strong>
              </div>

              <div className="order-info-item">
                <span>
                  {t("Project Type")}
                </span>

                <strong>
                  {t(order.order_type)}
                </strong>
              </div>

            </div>

            {order.status === "pending" && (
              <div className="pending-section">

                <div className="status-line" />

                <h3>
                  {t("Your order is under review")}
                </h3>

                <p>
                  {t(
                    "Our team is reviewing your project request. We will update your order once the review is complete."
                  )}
                </p>

              </div>
            )}

            {order.status === "in_progress" && (
              <div className="progress-section">

                <div className="status-line" />

                <h3>
                  {t("Your project is in progress")}
                </h3>

                <div className="project-details">

                  <div className="detail-box">
                    <span>
                      {t("Price")}
                    </span>

                    <strong>
                      {order.price ?? "-"}
                    </strong>
                  </div>

                  <div className="detail-box">
                    <span>
                      {t("Answer")}
                    </span>

                    <p>
                      {order.answer || "-"}
                    </p>
                  </div>

                  <div className="detail-box">
                    <span>
                      {t("Note")}
                    </span>

                    <p>
                      {order.note || "-"}
                    </p>
                  </div>

                </div>

                {!hasCustomerAnswer ? (
                  <div className="customer-answer-section">

                    <label htmlFor="customer-answer">
                      {t("Your Answer")}
                    </label>

                    <textarea
                      id="customer-answer"
                      value={customerAnswer}
                      onChange={(e) =>
                        setCustomerAnswer(
                          e.target.value
                        )
                      }
                      placeholder={t(
                        "Enter your answer here..."
                      )}
                      rows={5}
                      disabled={submittingAnswer}
                    />

                    <button
                      type="button"
                      className="answer-submit-button"
                      onClick={
                        handleSubmitAnswer
                      }
                      disabled={
                        submittingAnswer
                      }
                    >
                      {submittingAnswer
                        ? t("Submitting...")
                        : t("Submit Answer")}
                    </button>

                  </div>
                ) : (
                  <div className="answer-submitted-section">

                    <span className="eyebrow">
                      {t("// RESPONSE SUBMITTED")}
                    </span>

                    <h3>
                      {t("Your answer has been submitted")}
                    </h3>

                    <p>
                      {t(
                        "Your response has been received. You cannot submit another answer for this order."
                      )}
                    </p>

                  </div>
                )}

                {successMessage && (
                  <div className="ordertracking-message success-message">
                    {successMessage}
                  </div>
                )}

              </div>
            )}

            {order.status !== "pending" &&
              order.status !== "in_progress" && (
                <div className="other-status-section">

                  <div className="status-line" />

                  <h3>
                    {t("Order Status")}
                  </h3>

                  <p>
                    {t(
                      "Your order status has been updated. Please check again later for more information."
                    )}
                  </p>

                </div>
              )}

          </div>
        )}

        

        <div
          className="bottom-social-row reveal"
          style={{
            "--delay": "0.6s",
          }}
        >

          <a
            href="https://www.facebook.com/EmkaCode.tech"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FaFacebookF />
          </a>

          <a
            href="https://www.instagram.com/emkacode/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <FaInstagram />
          </a>

        </div>

        <div
          className="ordertracking-system-footer reveal"
          style={{
            "--delay": "0.7s",
          }}
        >
          {t(
            "EMKA_CORE // ORDER SYSTEM ACTIVE"
          )}
        </div>

      </main>
    </div>
  );
};

export default OrderTrackingPage;